import React, { useState, useEffect, useRef } from 'react';
import { getStorage, ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { useAuth } from '../context/AuthContext';
import { getMenu, saveMenuVoce, deleteMenuVoce, updateBeach } from '../services/firebase';
import Topbar from '../components/layout/Topbar';
import { Button, Spinner } from '../components/ui';

// ─── Upload immagine voce menu ────────────────────────────────────────────────
async function uploadMenuImage(beachCode, file, onProgress) {
    const storage = getStorage();
    const ext = file.name.split('.').pop();
    const path = `lidi/${beachCode}/menu/${Date.now()}.${ext}`;
    const storageRef = ref(storage, path);
    return new Promise((resolve, reject) => {
        const task = uploadBytesResumable(storageRef, file);
        task.on('state_changed',
            snap => onProgress && onProgress(Math.round(snap.bytesTransferred / snap.totalBytes * 100)),
            reject,
            async () => { resolve({ url: await getDownloadURL(task.snapshot.ref), path }); }
        );
    });
}

async function deleteMenuImage(path) {
    if (!path) return;
    try {
        const storage = getStorage();
        await deleteObject(ref(storage, path));
    } catch (e) { /* già eliminata */ }
}

// Categorie usate per raggruppare le voci nel menu (admin e mobile devono
// usare gli stessi valori — sono salvati come stringhe sul doc voce).
const CATEGORIE = ['Bevande', 'Pranzo', 'Snack', 'Gelati', 'Servizi', 'Altro'];

const inputStyle = {
    background: 'var(--surface)', border: '1px solid var(--border-mid)',
    borderRadius: 'var(--radius-md)', color: '#fff',
    padding: '8px 12px', fontSize: '13px', fontFamily: 'var(--font-sans)',
    width: '100%', boxSizing: 'border-box',
};

// ─── Form voce ────────────────────────────────────────────────────────────────
function VoceForm({ voce, onSave, onCancel, saving }) {
    const isNew = !voce.id;
    const [form, setForm] = useState({
        nome: voce.nome || '',
        descrizione: voce.descrizione || '',
        categoria: voce.categoria || 'Bevande',
        // Prezzo in EUR come number — convertito da string al cambio. Non
        // permettiamo 0: una voce a zero ha più senso come "Servizi" (gratis,
        // ma allora va in un'altra categoria) o come refuso.
        prezzo: voce.prezzo ?? '',
        immagineUrl: voce.immagineUrl || '',
        immaginePath: voce.immaginePath || '',
        disponibile: voce.disponibile !== false,
    });
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const fileRef = useRef(null);
    const { admin } = useAuth();

    const handleImageUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!file.type.startsWith('image/')) { alert('Seleziona un file immagine'); return; }
        if (file.size > 5 * 1024 * 1024) { alert('Immagine troppo grande (max 5MB)'); return; }
        setUploading(true);
        try {
            if (form.immaginePath) await deleteMenuImage(form.immaginePath);
            const result = await uploadMenuImage(admin.beachCode, file, setUploadProgress);
            setForm(p => ({ ...p, immagineUrl: result.url, immaginePath: result.path }));
        } catch (err) {
            alert('Errore upload immagine');
        } finally {
            setUploading(false);
            setUploadProgress(0);
        }
    };

    const handleRemoveImage = async () => {
        if (!window.confirm("Rimuovere l'immagine?")) return;
        await deleteMenuImage(form.immaginePath);
        setForm(p => ({ ...p, immagineUrl: '', immaginePath: '' }));
    };

    const setF = k => e => setForm(p => ({ ...p, [k]: e.target.value }));

    const handleSave = () => {
        // Normalizzo il prezzo: stringa → number con 2 decimali. Se invalid
        // o ≤ 0 blocco il save lato client (il bottone è già disabled, ma
        // doppia guard per evitare NaN in Firestore).
        const prezzo = parseFloat(form.prezzo);
        if (Number.isNaN(prezzo) || prezzo <= 0) {
            alert('Inserisci un prezzo valido (> 0)');
            return;
        }
        onSave({ ...form, prezzo: Math.round(prezzo * 100) / 100 });
    };

    return (
        <div style={{
            background: 'var(--navy-mid)', border: '1px solid var(--border)',
            borderRadius: 'var(--radius-lg)', padding: '24px', marginBottom: '16px',
        }}>
            <div style={{ fontSize: '15px', fontWeight: 500, marginBottom: '20px', fontFamily: 'var(--font-serif)' }}>
                {isNew ? 'Nuova voce' : `Modifica — ${voce.nome}`}
            </div>

            {/* Nome */}
            <div style={{ marginBottom: '14px' }}>
                <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                    Nome *
                </label>
                <input placeholder="es. Spritz Aperol" value={form.nome} onChange={setF('nome')} style={inputStyle} />
            </div>

            {/* Categoria + Prezzo */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '14px' }}>
                <div>
                    <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                        Categoria *
                    </label>
                    <select value={form.categoria} onChange={setF('categoria')} style={inputStyle}>
                        {CATEGORIE.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                </div>
                <div>
                    <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                        Prezzo (€) *
                    </label>
                    <input type="number" step="0.50" min="0" placeholder="es. 6.50" value={form.prezzo} onChange={setF('prezzo')} style={inputStyle} />
                </div>
            </div>

            {/* Descrizione */}
            <div style={{ marginBottom: '14px' }}>
                <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                    Descrizione
                </label>
                <textarea
                    rows={2}
                    placeholder="Ingredienti, dettagli per il cliente..."
                    value={form.descrizione}
                    onChange={setF('descrizione')}
                    style={{ ...inputStyle, resize: 'vertical', minHeight: '60px' }}
                />
            </div>

            {/* Immagine */}
            <div style={{ marginBottom: '20px' }}>
                <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                    Immagine (opzionale)
                </label>

                {form.immagineUrl && (
                    <div style={{ position: 'relative', marginBottom: '10px', borderRadius: '10px', overflow: 'hidden' }}>
                        <img src={form.immagineUrl} alt="preview"
                            style={{ width: '100%', maxHeight: '180px', objectFit: 'cover', display: 'block' }}
                        />
                        <button
                            onClick={handleRemoveImage}
                            style={{
                                position: 'absolute', top: '8px', right: '8px',
                                background: 'rgba(0,0,0,0.6)', border: 'none', color: '#fff',
                                width: '28px', height: '28px', borderRadius: '50%', cursor: 'pointer',
                            }}
                        >✕</button>
                    </div>
                )}

                {!form.immagineUrl && (
                    <>
                        <div
                            onClick={() => !uploading && fileRef.current?.click()}
                            style={{
                                border: '1px dashed var(--border-mid)', borderRadius: '10px',
                                padding: '20px', textAlign: 'center', cursor: uploading ? 'default' : 'pointer',
                            }}
                        >
                            {uploading ? (
                                <div>
                                    <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '8px' }}>
                                        Caricamento... {uploadProgress}%
                                    </div>
                                    <div style={{ height: '4px', background: 'var(--border)', borderRadius: '2px', overflow: 'hidden' }}>
                                        <div style={{ height: '100%', width: `${uploadProgress}%`, background: 'var(--ocean)', transition: 'width 0.2s' }} />
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <div style={{ fontSize: '24px', marginBottom: '8px' }}>🖼️</div>
                                    <div style={{ fontSize: '13px', color: 'var(--text-soft)', marginBottom: '4px' }}>
                                        Clicca per caricare un'immagine
                                    </div>
                                    <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                                        JPG, PNG, WebP · Max 5MB
                                    </div>
                                </>
                            )}
                        </div>

                        {/* Alternativa: incolla URL */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: '10px 0' }}>
                            <div style={{ flex: 1, height: '1px', background: 'var(--border)' }} />
                            <span style={{ fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>oppure</span>
                            <div style={{ flex: 1, height: '1px', background: 'var(--border)' }} />
                        </div>
                        <input
                            type="url"
                            placeholder="Incolla URL immagine (es. https://…/foto.jpg)"
                            value={form.immagineUrlInput || ''}
                            onChange={e => setForm(p => ({ ...p, immagineUrlInput: e.target.value }))}
                            onBlur={e => {
                                const url = (e.target.value || '').trim();
                                if (url && /^https?:\/\//i.test(url)) {
                                    // Salva solo l'URL, senza path storage: la cancellazione
                                    // di questa voce non tenterà di rimuovere il file remoto.
                                    setForm(p => ({ ...p, immagineUrl: url, immaginePath: '', immagineUrlInput: '' }));
                                }
                            }}
                            style={inputStyle}
                        />
                    </>
                )}

                <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleImageUpload} />
            </div>

            {/* Disponibile */}
            <label style={{ fontSize: '12px', color: 'var(--text-soft)', display: 'flex', alignItems: 'flex-start', gap: '8px', cursor: 'pointer', marginBottom: '20px', lineHeight: 1.5 }}>
                <input type="checkbox" checked={form.disponibile} onChange={e => setForm(p => ({ ...p, disponibile: e.target.checked }))} style={{ marginTop: '2px' }} />
                <span>
                    Disponibile oggi
                    <span style={{ display: 'block', fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>
                        Disattiva quando finisce. La voce resta in menu ma viene mostrata come "esaurita".
                    </span>
                </span>
            </label>

            <div style={{ display: 'flex', gap: '8px' }}>
                <Button variant="primary" onClick={handleSave} disabled={!form.nome || !form.prezzo || saving}>
                    {saving ? <Spinner size={14} /> : isNew ? 'Crea voce' : 'Salva modifiche'}
                </Button>
                <Button variant="ghost" onClick={onCancel}>Annulla</Button>
            </div>
        </div>
    );
}

// ─── Icone categoria (riusate dal design system: stroke su currentColor) ──────
// Le path sono pensate per un viewBox 24×24 con stroke-width 2.
const CATEGORY_ICONS = {
    'Bevande': <path d="M5 3h14l-1 5a6 6 0 0 1-12 0L5 3Z M12 13v6 M8 21h8" />,
    'Pranzo': <path d="M3 2v7c0 1.1.9 2 2 2h0a2 2 0 0 0 2-2V2 M5 2v20 M11 2v6a3 3 0 0 0 3 3v0 M14 2v20 M18 2a3 3 0 0 0-3 3v6h6V5a3 3 0 0 0-3-3Z" />,
    'Snack': <path d="M3 11h18 M5 11V8a7 7 0 0 1 14 0v3 M4 11h16l-1 7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2l-1-7Z" />,
    'Gelati': <><path d="M8 14 5.5 21h13L16 14 M7 14h10a5 5 0 0 0-10 0Z" /><circle cx="12" cy="9" r="1" /></>,
    'Servizi': <path d="M12 2v3 M3 11l9-6 9 6 M5 11v10h14V11 M9 21v-5h6v5" />,
    'Altro': <><rect x="3" y="3" width="7" height="7" rx="1.5" /><rect x="14" y="3" width="7" height="7" rx="1.5" /><rect x="3" y="14" width="7" height="7" rx="1.5" /><rect x="14" y="14" width="7" height="7" rx="1.5" /></>,
};

// ─── CSS isolato della pagina (pattern già usato in Tariffe.jsx con keyframes).
//    Tenuto inline per non introdurre un file CSS separato — il blocco è
//    self-contained, niente leak di stili nelle altre pagine grazie ai prefissi
//    `.menu-page-*`.
const PAGE_CSS = `
.menu-page-wrap { padding: 28px 32px 60px; max-width: 1240px; margin: 0 auto; }
.menu-page-head { margin-bottom: 22px; }
.menu-page-head h1 { font-size: 26px; font-weight: 700; letter-spacing: -0.4px; color: var(--text); }
.menu-page-head p { color: var(--text-muted); font-size: 14.5px; margin-top: 6px; max-width: 680px; line-height: 1.55; }

/* Service banner */
.menu-svc {
  display: flex; align-items: center; gap: 18px;
  background: var(--surface); border: 1px solid var(--border);
  border-radius: 14px; padding: 18px 22px; margin-bottom: 22px;
  position: relative; overflow: hidden;
}
.menu-svc::before {
  content: ""; position: absolute; left: 0; top: 0; bottom: 0; width: 3px;
  background: var(--accent-color, #2b9fe9); transition: background-color .15s;
}
.menu-svc--on::before { background: #34c77b; }
.menu-svc--off::before { background: #5d6675; }
.menu-svc__dot {
  width: 11px; height: 11px; border-radius: 50%; flex: 0 0 11px;
  background: #34c77b; box-shadow: 0 0 0 4px rgba(52,199,123,.14);
}
.menu-svc--off .menu-svc__dot { background: #5d6675; box-shadow: 0 0 0 4px rgba(93,102,117,.18); }
.menu-svc__text { flex: 1; min-width: 0; }
.menu-svc__title { font-weight: 700; font-size: 15.5px; color: var(--text); }
.menu-svc__sub { color: var(--text-muted); font-size: 13.5px; margin-top: 3px; }
.menu-svc__meta {
  display: flex; align-items: center; gap: 6px; color: var(--text-muted); font-size: 12.5px;
  padding-right: 22px; border-right: 1px solid var(--border); margin-right: 4px;
}
.menu-svc__meta:last-of-type { border: none; padding-right: 0; margin-right: 0; }
.menu-svc__meta b { color: var(--text); font-weight: 700; }

/* Toggle switch */
.menu-switch { position: relative; width: 46px; height: 26px; flex: 0 0 46px; cursor: pointer; display: inline-block; }
.menu-switch input { display: none; }
.menu-switch__track { position: absolute; inset: 0; background: #34c77b; border-radius: 20px; transition: background .2s; }
.menu-switch__thumb { position: absolute; top: 3px; left: 23px; width: 20px; height: 20px; background: #fff; border-radius: 50%; transition: left .2s; box-shadow: 0 2px 4px rgba(0,0,0,.3); }
.menu-switch input:not(:checked) ~ .menu-switch__track { background: #2d3748; }
.menu-switch input:not(:checked) ~ .menu-switch__thumb { left: 3px; }
.menu-switch--busy { opacity: .55; pointer-events: none; }

/* Hours card */
.menu-hours {
  display: flex; align-items: center; gap: 14px;
  background: var(--surface); border: 1px solid var(--border);
  border-radius: 12px; padding: 12px 18px; margin-bottom: 22px;
}
.menu-hours__ic { color: var(--text-muted); display: grid; place-items: center; }
.menu-hours__ic svg { width: 18px; height: 18px; stroke-width: 1.9; }
.menu-hours__label { font-size: 13px; color: var(--text); font-weight: 600; flex: 1; min-width: 0; }
.menu-hours__hint { font-size: 11.5px; color: var(--text-muted); font-weight: 400; margin-left: 6px; }
.menu-hours__inputs { display: flex; align-items: center; gap: 8px; }
.menu-hours__input {
  background: var(--navy-mid); border: 1px solid var(--border);
  border-radius: 8px; color: var(--text);
  padding: 7px 10px; font-family: inherit; font-size: 13px;
  width: 95px; transition: .15s;
}
.menu-hours__input:focus { outline: none; border-color: #2b9fe9; }
.menu-hours__sep { color: var(--text-muted); font-size: 13px; }

/* Toolbar (chips + search + cta) */
.menu-toolbar { display: flex; align-items: center; justify-content: space-between; gap: 16px 20px; margin-bottom: 18px; flex-wrap: wrap; }
.menu-chips { display: flex; gap: 8px; flex-wrap: wrap; flex: 1 1 440px; min-width: 0; }
.menu-chip {
  display: inline-flex; align-items: center; gap: 8px;
  padding: 9px 15px; border-radius: 10px;
  background: var(--surface); border: 1px solid var(--border);
  color: var(--text-muted); font-size: 13.5px; font-weight: 600; cursor: pointer;
  font-family: inherit; transition: .15s;
}
.menu-chip:hover { color: var(--text); border-color: var(--border-mid); }
.menu-chip__n { font-size: 11.5px; font-weight: 700; background: var(--border); color: var(--text-muted); padding: 1px 7px; border-radius: 20px; line-height: 1.5; }
.menu-chip--active { background: rgba(43,159,233,.14); border-color: rgba(43,159,233,.45); color: #9ed4f7; }
.menu-chip--active .menu-chip__n { background: rgba(43,159,233,.25); color: #bfe2fa; }

.menu-actions { display: flex; align-items: center; gap: 11px; }
.menu-search { position: relative; }
.menu-search svg { position: absolute; left: 13px; top: 50%; transform: translateY(-50%); width: 17px; height: 17px; color: var(--text-muted); stroke-width: 2; }
.menu-search input {
  width: 230px; padding: 10px 14px 10px 39px; border-radius: 11px;
  background: var(--surface); border: 1px solid var(--border);
  color: var(--text); font-family: inherit; font-size: 14px; transition: .15s;
}
.menu-search input::placeholder { color: var(--text-muted); }
.menu-search input:focus { outline: none; border-color: #2b9fe9; }

/* Table */
.menu-table { background: var(--surface); border: 1px solid var(--border); border-radius: 14px; overflow: hidden; }
.menu-table__head, .menu-row { display: grid; grid-template-columns: 64px 1fr 130px 150px 96px; align-items: center; gap: 18px; padding: 13px 22px; }
.menu-table__head { border-bottom: 1px solid var(--border); font-size: 11px; letter-spacing: .1em; text-transform: uppercase; color: var(--text-muted); font-weight: 700; }
.menu-table__head .ta-right { text-align: right; }
.menu-table__head .ta-center { text-align: center; }

.menu-catblock { border-bottom: 1px solid var(--border); }
.menu-catblock:last-child { border-bottom: none; }
.menu-catblock__head {
  display: flex; align-items: center; gap: 11px;
  padding: 15px 22px 11px; background: var(--navy-mid);
}
.menu-catblock__ic {
  width: 30px; height: 30px; border-radius: 9px; display: grid; place-items: center;
  background: rgba(43,159,233,.14); color: #2b9fe9; flex: 0 0 30px;
}
.menu-catblock__ic svg { width: 17px; height: 17px; }
.menu-catblock__title { font-size: 14.5px; font-weight: 700; letter-spacing: .2px; color: var(--text); }
.menu-catblock__n { color: var(--text-muted); font-size: 13px; font-weight: 600; }

.menu-row { border-top: 1px solid var(--border); transition: .13s; }
.menu-catblock .menu-row:first-of-type { border-top: none; }
.menu-row:hover { background: var(--navy-mid); }
.menu-row--out .menu-row__name, .menu-row--out .menu-row__price, .menu-row--out .menu-row__thumb { opacity: .55; }

.menu-row__thumb { width: 48px; height: 48px; border-radius: 10px; overflow: hidden; flex: 0 0 48px; display: grid; place-items: center; background: var(--border); background-size: cover; background-position: center; }
.menu-row__thumb .ph { color: var(--text-muted); font-size: 22px; }
.menu-row__name { font-weight: 600; font-size: 15px; color: var(--text); }
.menu-row__desc { color: var(--text-muted); font-size: 13px; margin-top: 3px; line-height: 1.45; overflow: hidden; text-overflow: ellipsis; display: -webkit-box; -webkit-line-clamp: 1; -webkit-box-orient: vertical; }
.menu-row__price { font-weight: 700; font-size: 15.5px; text-align: right; font-variant-numeric: tabular-nums; color: var(--text); }

.menu-pill { display: inline-flex; align-items: center; gap: 7px; padding: 5px 11px; border-radius: 20px; font-size: 12.5px; font-weight: 600; white-space: nowrap; }
.menu-pill::before { content: ""; width: 7px; height: 7px; border-radius: 50%; }
.menu-pill--ok { background: rgba(52,199,123,.14); color: #34c77b; }
.menu-pill--ok::before { background: #34c77b; }
.menu-pill--out { background: rgba(236,106,106,.13); color: #ec6a6a; }
.menu-pill--out::before { background: #ec6a6a; }
.menu-row__status { display: flex; justify-content: center; }

.menu-row__actions { display: flex; align-items: center; justify-content: flex-end; gap: 4px; }
.menu-iconbtn {
  width: 34px; height: 34px; border-radius: 9px; border: none; background: transparent;
  color: var(--text-muted); display: grid; place-items: center; cursor: pointer; transition: .13s;
}
.menu-iconbtn:hover { background: var(--border); color: var(--text); }
.menu-iconbtn--danger:hover { background: rgba(236,106,106,.13); color: #ec6a6a; }
.menu-iconbtn svg { width: 17px; height: 17px; stroke-width: 1.9; }

.menu-empty { padding: 48px; text-align: center; color: var(--text-muted); font-size: 14px; }

@media (max-width: 1080px) {
  .menu-table__head, .menu-row { grid-template-columns: 56px 1fr 110px 130px 80px; gap: 12px; padding-left: 16px; padding-right: 16px; }
  .menu-search input { width: 170px; }
}
@media (max-width: 720px) {
  .menu-table__head { display: none; }
  .menu-row { grid-template-columns: 56px 1fr auto; }
  .menu-row__price, .menu-row__status, .menu-row__actions { grid-column: 2 / -1; }
  .menu-row__price { text-align: left; }
}
`;

// ─── Pagina principale ────────────────────────────────────────────────────────
export default function Menu() {
    const { admin, beach, refreshBeach } = useAuth();
    const [voci, setVoci] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);
    const [saving, setSaving] = useState(false);
    const [filtroCategoria, setFiltroCategoria] = useState('Tutte');
    const [togglingService, setTogglingService] = useState(false);
    const [query, setQuery] = useState('');
    // Local state degli input orario: tenuto separato dal beach perché l'utente
    // può digitare valori temporaneamente "invalidi" (es. cancellando i campi)
    // — sincronizziamo solo on blur. Default 09:00–19:00 se il lido non li ha
    // ancora settati: copre la giornata tipica di un lido.
    const [oraInizio, setOraInizio] = useState(beach?.ordineOmbrelloneDalle ?? '09:00');
    const [oraFine, setOraFine] = useState(beach?.ordineOmbrelloneAlle ?? '19:00');

    // Riallinea i campi locali quando beach viene aggiornato dall'esterno
    // (es. dopo refreshBeach). Solo se siamo fuori focus, altrimenti
    // sovrascriveremmo l'input mentre l'utente digita.
    useEffect(() => {
        setOraInizio(beach?.ordineOmbrelloneDalle ?? '09:00');
        setOraFine(beach?.ordineOmbrelloneAlle ?? '19:00');
    }, [beach?.ordineOmbrelloneDalle, beach?.ordineOmbrelloneAlle]);

    const handleSaveOrari = async (nuovoDalle, nuovoAlle) => {
        // Validazione minima: range non vuoto e dalle < alle (string compare
        // funziona per HH:MM perché lex order coincide con time order).
        if (!nuovoDalle || !nuovoAlle) return;
        if (nuovoDalle >= nuovoAlle) {
            alert('L\'orario di chiusura deve essere successivo all\'apertura.');
            // Restore last good values
            setOraInizio(beach?.ordineOmbrelloneDalle ?? '09:00');
            setOraFine(beach?.ordineOmbrelloneAlle ?? '19:00');
            return;
        }
        // No-op se invariato
        if (nuovoDalle === beach?.ordineOmbrelloneDalle && nuovoAlle === beach?.ordineOmbrelloneAlle) return;
        try {
            await updateBeach(admin.beachCode, {
                ordineOmbrelloneDalle: nuovoDalle,
                ordineOmbrelloneAlle: nuovoAlle,
            });
            await refreshBeach();
        } catch (err) {
            alert(`Errore: ${err.message ?? 'impossibile salvare gli orari'}`);
        }
    };

    // Master switch del servizio: salvato su lidi/{beachCode}.ordineOmbrelloneAttivo.
    // L'app mobile mostrerà l'opzione "Ordina al tavolo" SOLO se questo è true.
    // Cambio è ottimistico per UX reattiva — la chiamata updateBeach + refresh
    // è dietro, ma il banner di stato resta consistente perché beach è
    // refreshato a fine call.
    const serviceActive = beach?.ordineOmbrelloneAttivo === true;

    const handleToggleService = async () => {
        if (togglingService) return;
        const nuovoStato = !serviceActive;
        // Confirm solo quando si attiva senza voci nel menu: altrimenti il
        // toggle è una swap di pochi byte, niente cerimoniale.
        if (nuovoStato && voci.length === 0) {
            if (!window.confirm('Il menu è vuoto. Vuoi attivare il servizio comunque? I clienti non vedranno voci da ordinare finché non ne aggiungi.')) return;
        }
        setTogglingService(true);
        try {
            await updateBeach(admin.beachCode, { ordineOmbrelloneAttivo: nuovoStato });
            await refreshBeach();
        } catch (err) {
            alert(`Errore: ${err.message ?? 'impossibile aggiornare lo stato'}`);
        } finally {
            setTogglingService(false);
        }
    };

    useEffect(() => {
        if (!admin?.beachCode) return;
        getMenu(admin.beachCode)
            .then(setVoci)
            .finally(() => setLoading(false));
    }, [admin?.beachCode]);

    const handleSave = async (form) => {
        setSaving(true);
        try {
            await saveMenuVoce(admin.beachCode, editing?.id ? { ...form, id: editing.id } : form);
            // Ricarico la lista invece di patchare in place: il costo è basso
            // (menu raramente > 100 voci) e mantiene la lista coerente con DB.
            const nuove = await getMenu(admin.beachCode);
            setVoci(nuove);
            setEditing(null);
        } finally { setSaving(false); }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Eliminare questa voce dal menu? Gli ordini già fatti la conservano (sono "congelati" al prezzo del momento).')) return;
        // Elimino anche l'immagine se presente per non lasciare file orfani
        // nello storage. Best-effort: se l'immagine fosse già stata cancellata
        // l'errore viene silenziato nel helper.
        const voce = voci.find(v => v.id === id);
        if (voce?.immaginePath) await deleteMenuImage(voce.immaginePath);
        await deleteMenuVoce(admin.beachCode, id);
        setVoci(voci.filter(v => v.id !== id));
    };

    const voceNuova = { nome: '', categoria: 'Bevande', prezzo: '' };

    // Conteggi per banner di servizio + chip count badge.
    const totalePubblicate = voci.length;
    const totaleEsaurite = voci.filter(v => v.disponibile === false).length;
    const countByCategoria = CATEGORIE.reduce((acc, c) => {
        acc[c] = voci.filter(v => v.categoria === c).length;
        return acc;
    }, {});

    // Pipeline filtro: prima per categoria attiva, poi per query (case-insensitive)
    // sul nome o sulla descrizione — match parziale per UX da search.
    const queryLower = query.trim().toLowerCase();
    const vociFiltered = voci
        .filter(v => filtroCategoria === 'Tutte' || v.categoria === filtroCategoria)
        .filter(v => !queryLower || v.nome.toLowerCase().includes(queryLower) || (v.descrizione || '').toLowerCase().includes(queryLower));

    const grouped = CATEGORIE
        .map(cat => ({
            categoria: cat,
            items: vociFiltered.filter(v => v.categoria === cat).sort((a, b) => a.nome.localeCompare(b.nome)),
        }))
        .filter(g => g.items.length > 0);

    return (
        <>
            <style>{PAGE_CSS}</style>
            <Topbar />
            <div className="menu-page-wrap">

                {/* Page head */}
                <div className="menu-page-head">
                    <h1>Menu</h1>
                    <p>Cibo, bevande e servizi che i clienti possono ordinare dall'app, direttamente dal proprio ombrellone.</p>
                </div>

                {/* Service banner: master switch del servizio per la struttura.
                    Quando OFF gli ospiti non vedono l'opzione "Ordina al tavolo"
                    nell'app, indipendentemente da quante voci ci sono in catalogo. */}
                <div className={`menu-svc ${serviceActive ? 'menu-svc--on' : 'menu-svc--off'}`}>
                    <span className="menu-svc__dot" />
                    <div className="menu-svc__text">
                        <div className="menu-svc__title">
                            {serviceActive ? 'Servizio attivo' : 'Servizio disattivato'}
                        </div>
                        <div className="menu-svc__sub">
                            {serviceActive
                                ? 'I clienti possono ordinare dal menu in tempo reale.'
                                : 'Il menu non è raggiungibile dai clienti.'}
                        </div>
                    </div>
                    {totalePubblicate > 0 && (
                        <>
                            <div className="menu-svc__meta"><span><b>{totalePubblicate}</b>{' '}voci pubblicate</span></div>
                            <div className="menu-svc__meta"><span><b>{totaleEsaurite}</b>{' '}esaurite oggi</span></div>
                        </>
                    )}
                    <label className={`menu-switch ${togglingService ? 'menu-switch--busy' : ''}`} aria-label="Attiva o disattiva il servizio">
                        <input
                            type="checkbox"
                            checked={serviceActive}
                            onChange={handleToggleService}
                            disabled={togglingService}
                        />
                        <span className="menu-switch__track" />
                        <span className="menu-switch__thumb" />
                    </label>
                </div>

                {/* Range orario: il mobile blocca gli ordini fuori da questa
                    finestra. Salvataggio on blur — niente bottone esplicito,
                    e validazione lato handler (dalle < alle). */}
                <div className="menu-hours">
                    <span className="menu-hours__ic">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                            <circle cx="12" cy="12" r="10" />
                            <path d="M12 6v6l4 2" />
                        </svg>
                    </span>
                    <div className="menu-hours__label">
                        Orario del servizio
                        <span className="menu-hours__hint">· i clienti potranno ordinare solo in questa finestra</span>
                    </div>
                    <div className="menu-hours__inputs">
                        <input
                            type="time"
                            className="menu-hours__input"
                            value={oraInizio}
                            onChange={e => setOraInizio(e.target.value)}
                            onBlur={() => handleSaveOrari(oraInizio, oraFine)}
                        />
                        <span className="menu-hours__sep">→</span>
                        <input
                            type="time"
                            className="menu-hours__input"
                            value={oraFine}
                            onChange={e => setOraFine(e.target.value)}
                            onBlur={() => handleSaveOrari(oraInizio, oraFine)}
                        />
                    </div>
                </div>

                {/* Toolbar: chip filtri + search + CTA aggiungi voce */}
                <div className="menu-toolbar">
                    <div className="menu-chips">
                        <button
                            type="button"
                            className={`menu-chip ${filtroCategoria === 'Tutte' ? 'menu-chip--active' : ''}`}
                            onClick={() => setFiltroCategoria('Tutte')}
                        >
                            Tutte <span className="menu-chip__n">{totalePubblicate}</span>
                        </button>
                        {CATEGORIE.map(cat => (
                            <button
                                type="button"
                                key={cat}
                                className={`menu-chip ${filtroCategoria === cat ? 'menu-chip--active' : ''}`}
                                onClick={() => setFiltroCategoria(cat)}
                                // Categorie senza voci nascoste finché non ci sono voci di quella
                                // categoria — evita una toolbar piena di chip a zero.
                                style={{ display: countByCategoria[cat] === 0 ? 'none' : undefined }}
                            >
                                {cat} <span className="menu-chip__n">{countByCategoria[cat]}</span>
                            </button>
                        ))}
                    </div>
                    <div className="menu-actions">
                        <div className="menu-search">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                <path d="M21 21l-4.3-4.3M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16Z" />
                            </svg>
                            <input
                                type="text"
                                placeholder="Cerca una voce…"
                                value={query}
                                onChange={e => setQuery(e.target.value)}
                            />
                        </div>
                        {!editing && (
                            <Button variant="primary" onClick={() => setEditing(voceNuova)}>
                                + Aggiungi voce
                            </Button>
                        )}
                    </div>
                </div>

                {/* Form di edit/create (overlay inline come prima) */}
                {editing && (
                    <VoceForm
                        voce={editing}
                        onSave={handleSave}
                        onCancel={() => setEditing(null)}
                        saving={saving}
                    />
                )}

                {/* Tabella menu */}
                <div className="menu-table">
                    <div className="menu-table__head">
                        <div></div>
                        <div>Voce</div>
                        <div className="ta-right">Prezzo</div>
                        <div className="ta-center">Disponibilità</div>
                        <div></div>
                    </div>

                    {loading ? (
                        <div className="menu-empty"><Spinner /></div>
                    ) : voci.length === 0 ? (
                        <div className="menu-empty">
                            Nessuna voce nel menu. Aggiungine una per consentire ai clienti di ordinare.
                        </div>
                    ) : grouped.length === 0 ? (
                        <div className="menu-empty">
                            Nessuna voce trovata{queryLower ? ` per "${query}"` : ''}.
                        </div>
                    ) : (
                        grouped.map(g => (
                            <div className="menu-catblock" key={g.categoria}>
                                <div className="menu-catblock__head">
                                    <span className="menu-catblock__ic">
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            {CATEGORY_ICONS[g.categoria]}
                                        </svg>
                                    </span>
                                    <h3 className="menu-catblock__title">{g.categoria}</h3>
                                    <span className="menu-catblock__n">· {g.items.length}</span>
                                </div>
                                {g.items.map(v => (
                                    <div className={`menu-row ${v.disponibile === false ? 'menu-row--out' : ''}`} key={v.id}>
                                        <div
                                            className="menu-row__thumb"
                                            style={v.immagineUrl ? { backgroundImage: `url(${v.immagineUrl})` } : undefined}
                                        >
                                            {!v.immagineUrl && <span className="ph">🍽️</span>}
                                        </div>
                                        <div style={{ minWidth: 0 }}>
                                            <div className="menu-row__name">{v.nome}</div>
                                            {v.descrizione && <div className="menu-row__desc">{v.descrizione}</div>}
                                        </div>
                                        <div className="menu-row__price">€ {Number(v.prezzo).toFixed(2)}</div>
                                        <div className="menu-row__status">
                                            <span className={`menu-pill ${v.disponibile === false ? 'menu-pill--out' : 'menu-pill--ok'}`}>
                                                {v.disponibile === false ? 'Esaurita' : 'Disponibile'}
                                            </span>
                                        </div>
                                        <div className="menu-row__actions">
                                            <button
                                                className="menu-iconbtn"
                                                title="Modifica"
                                                onClick={() => setEditing(v)}
                                            >
                                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                                    <path d="M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
                                                </svg>
                                            </button>
                                            <button
                                                className="menu-iconbtn menu-iconbtn--danger"
                                                title="Elimina"
                                                onClick={() => handleDelete(v.id)}
                                            >
                                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                                    <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6M10 11v6M14 11v6" />
                                                </svg>
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ))
                    )}
                </div>
            </div>
        </>
    );
}
